<template>
    <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
    <div class="spinner" v-if='page.isLoading'></div>
    <div class="container">
        <div class="custom-breadcrumb">
            <o-bread-crumb>
                <template v-slot:pageName>
                    Merchant Setting
                </template>
                <template v-slot:pageDetail>
                    Authentication URL
                </template>
                <template v-slot:pageDescription>
                    <template v-if="isMerchantAccessTokenEnabled">
                        <h5>Authentication URL</h5>
                        You can generate a personal Authentication URL for each application
                        <br> you use that needs access to the Membership API.
                    </template>
                </template>
            </o-bread-crumb>
        </div>

        <div v-if="level.state">
            <div class="alert alert-danger mt-1" role="alert">
                Invalid level, only access can update authentication URL!
            </div>
        </div>
        <div v-else-if="validation && !successNotification">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>
        <div v-if="successNotification">
            <div class="alert alert-success mt-1" role="alert">
                Authentication URL has been successfully generated!
            </div>
        </div>
        <div v-if="successSave">
            <div class="alert alert-success mt-1" role="alert">
                Authentication URL has been successfully saved!
            </div>
        </div>

        <!-- Feature Disabled State -->
        <div v-if="!isMerchantAccessTokenEnabled" class="feature-disabled-container mt-4">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Authentication URL Disabled</h4>
                <p>The Authentication URL feature has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isMerchantAccessTokenEnabled">
        <o-card v-if="thisComponentShow" :card-title="'Authentication URL'" class="custom-table">
            <div class="form-token" v-if="write">
                <div class="create-token">
                    <o-form-group title="Authentication URL" name="url">
                        <span class="text-danger"> *</span>
                        <o-input-text 
                            v-model="token.url" 
                            type="text" 
                            :required="true" 
                            :autocomplete="'off'" 
                            style="flex-grow: 1; min-width: 300px;"
                        />
                    </o-form-group>

                    <div v-if="token.url && !urlGenerated && token.checking_url == false">
                        <o-button 
                            @click="generateURL" 
                            type="button" 
                            :size="'sm'" 
                            :variant="'primary'" 
                            :white="'white'"
                        >
                            Generate URL
                        </o-button>
                        <p class="text-muted" style="margin-top: 5px;">
                            <span class="text-danger"> *</span>For Omni Hotelier Integration with Booking Engine
                        </p>
                    </div>

                    <div v-if="token.url && (urlGenerated || token.checking_url == true)">
                        <o-form-group title="Change Property ID" name="changePropertyId">
                            <o-input-text 
                                v-model="propertyId" 
                                type="text" 
                                :required="true" 
                                :autocomplete="'off'" 
                                :placeholder="'Enter only the Property ID'" 
                                style="flex-grow: 1; min-width: 300px;"
                            />
                        </o-form-group>
                        
                        <o-button 
                            @click="generateURL" 
                            type="button" 
                            :size="'sm'" 
                            :variant="'primary'" 
                            :white="'white'" 
                            :disabled="!propertyId"
                        >
                            Generate URL
                        </o-button>
                        <p class="text-muted" style="margin-top: 5px;">
                            <span class="text-danger"> *</span>For Omni Hotelier Integration with Booking Engine
                        </p>
                    </div>

                    <o-button
                        type="submit" 
                        @click="createAuthenticationURL()" 
                        :size="'sm'" 
                        :variant="'primary'" 
                        :white="'white'">
                        Save Authentication URL Merchant
                    </o-button>
                </div>
            </div>
        </o-card>
        </template>
    </div>
</template>

<script>
import { OCard, OFormGroup, OInputText, OButton, OBreadCrumb } from '@/components/OmnihControl';
import { reactive, ref } from '@vue/reactivity';
import { onMounted } from '@vue/runtime-core';
import { useStore } from 'vuex';
import { watch, computed } from 'vue';

export default {
    components: {
        OCard,
        OBreadCrumb,
        OFormGroup,
        OInputText,
        OButton,
    },
    setup() {
        const store = useStore();

        const isMerchantAccessTokenEnabled = computed(() => {
            const flag = store.getters['FeatureFlag/getMerchantAccessTokenFlag'];
            return flag !== 'NONACTIVE';
        });

        const page = reactive({
            isLoading: true,
            // component: 'authentication-url'
            component: 'merchant'
        });

        const token = reactive({
            url: '',
            checking_url: '',
        });

        const propertyId = ref(''); 
        const urlGenerated = ref(false);

        const level = reactive({
            state: false,
        });

        const new_token = ref("");
        const validation = ref('');

        const button = reactive({
            status: "Copy to clipboard"
        });

        const thisComponentShow = ref(true);
        const selfLoaded = ref(store.state.Auth.selfLoaded);
        const write = ref(false);

        const selectedBusinessProfile = ref({
            corporate_id: null,
            branch_id: null,
            merchant_id: null,
        });
        const bookingEngineApi = ref('');

        const successNotification = ref(false);

        const successSave = ref(false);

        watch(
            () => store.state.Auth.selfLoaded,
            (newVal) => {
                if (newVal) {
                    selfLoaded.value = newVal;
                    store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                        selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                        selectedBusinessProfile.branch_id = businessProfile.branch_id;
                        selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                        loadTokens(selectedBusinessProfile.merchant_id);
                    });
                }
            }
        );

        onMounted(async () => {
            page.isLoading = true;

            try {
                if (selfLoaded.value) {
                    const businessProfile = await store.dispatch('Auth/getBusinessProfile');
                    selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                    selectedBusinessProfile.branch_id = businessProfile.branch_id;
                    selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                    await loadTokens(selectedBusinessProfile.merchant_id); 
                }

                const data = await store.dispatch('Publics/get_booking_engine_api');
                bookingEngineApi.value = data;
            } catch (error) {
                console.error('Error loading data on mount:', error);
            } finally {
                page.isLoading = false;
            }
        });

        const generateURL = () => {
            successSave.value = false;
            successNotification.value = false;
            if (bookingEngineApi.value) {
                const id = propertyId.value.trim() !== '' ? propertyId.value : token.url;
                token.url = `${bookingEngineApi.value}${id}/`;
                urlGenerated.value = true;
                successNotification.value = true;
            } else {
                console.error('Booking Engine API value is empty.');
            }
        };

        const updateUrlWithNewId = () => {
            if (propertyId.value) {
                token.url = token.url.replace(/[^/]*$/, propertyId.value);
            }
        };
        const createAuthenticationURL = () => {
            page.isLoading = true;
            successSave.value = false;
            successNotification.value = false;
            const data = { 
                id: selectedBusinessProfile.merchant_id,
                merchant_id: selectedBusinessProfile.merchant_id,
                branch_id: selectedBusinessProfile.branch_id,
                commerce_site: token.url,
                token,
            };

            store.dispatch('Merchant/update_commerce', { data })
            .then(() => {
                new_token.value = "";
                successSave.value = true;
            })
            .catch((err) => {
                console.log(err);
                if (err.response.data.error === "ACCESS_TOKEN.CREATE.DOMAIN.EXCEPTION.INVALID.LEVEL") {
                    level.state = true;
                } else if (err.response.data.message && err.response.status === 403) {
                    validation.value = err.response.data.message;
                }
            })
            .finally(() => {
                page.isLoading = false; 
                loadTokens(selectedBusinessProfile.merchant_id);
            });
        };

        const loadTokens = (id) => {
            page.isLoading = true;

            store.dispatch('Merchant/read_single', id)
                .then((response) => {
                    const data = response;

                    if (data.business_profile && data.business_profile.commerce_site) {
                        token.url = data.business_profile.commerce_site;
                        token.checking_url = true;
                    } else {
                        console.warn("Commerce site data not found in response");
                    }
                })
                .catch((err) => {
                    console.log('Response error', err);
                    validation.value = err.response?.data?.message || "An error occurred";
                    thisComponentShow.value = false;
                    console.error(err);
                })
                .finally(() => {
                    page.isLoading = false;
                });

            if (store.state.Auth.currUser.level === 'root') {
                write.value = true;
            } else {
                const userModules = store.state.Auth.userInfo.modules;

                if (userModules) {
                    const matchingModule = userModules.find(
                        (module) => module.module_name === page.component
                    );
                    write.value = matchingModule ? matchingModule.write : false;
                } else {
                    console.error('userModules is undefined');
                    write.value = false;
                }
                console.log('checking userModules', userModules, write.value);
            }
        };

        return {
            page,
            token,
            new_token,
            button,
            createAuthenticationURL,
            level,
            validation,
            thisComponentShow,
            write,
            generateURL,
            propertyId,
            updateUrlWithNewId,
            successNotification,
            urlGenerated,
            successSave,
            isMerchantAccessTokenEnabled
        }
    },
}
</script>

<style scoped>
.card.custom-table {
    width: 800px;
}

.form-token {
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
}

.form-token .create-token {
    width: 100%; /* Atur untuk tampilan penuh */
    margin-right: 10px;
}

.container .form-token button.btn {
    margin-top: 10px !important;
    padding: 0.7rem 3.5rem 0.7rem 3.5rem;
    width: 100%;
}

.container .form-token .token-info input.form-control {
    font-size: 12px !important;
    width: 340px;
    height: 47.2px;
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}
</style>